import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, Home } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => `€${(Number(n) || 0).toFixed(2)}`;

function getParam(name) {
  return new URLSearchParams(window.location.search).get(name);
}

export default function DepositSuccess() {
  const [state, setState] = useState({ phase: "loading" }); // loading | paid | pending | expired | error | missing
  const attempts = useRef(0);

  const poll = useCallback(async (sessionId) => {
    if (attempts.current >= 6) {
      setState((s) => (s.phase === "paid" ? s : { phase: "pending" }));
      return;
    }
    attempts.current += 1;
    try {
      const { data } = await axios.get(`${API}/deposit/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setState({ phase: "paid", amount: data.amount_eur, ref: data.booking_ref });
        return;
      }
      if (data.status === "expired") {
        setState({ phase: "expired" });
        return;
      }
      setTimeout(() => poll(sessionId), 2000);
    } catch (e) {
      setState({ phase: "error", message: e?.response?.data?.detail || "Could not verify payment." });
    }
  }, []);

  useEffect(() => {
    const sessionId = getParam("session_id");
    if (!sessionId) {
      setState({ phase: "missing" });
      return;
    }
    poll(sessionId);
  }, [poll]);

  return (
    <div data-testid="deposit-success-page" className="min-h-screen bg-[#050505] text-zinc-100">
      <Header />

      <main className="pt-28 pb-24 px-6">
        <div className="max-w-xl mx-auto text-center">
          {state.phase === "loading" && (
            <div className="flex flex-col items-center gap-4 py-16">
              <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin" />
              <p className="text-sm text-zinc-400">Verifying your payment…</p>
            </div>
          )}

          {state.phase === "paid" && (
            <div data-testid="deposit-paid-panel" className="py-8">
              <div className="w-20 h-20 mx-auto rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-emerald-400" />
              </div>
              <h1 className="mt-8 font-display text-4xl sm:text-5xl text-white font-light">
                Deposit received
              </h1>
              <p className="mt-4 text-zinc-400">
                Thank you! We&rsquo;ve secured your booking{state.ref ? ` (${state.ref})` : ""}. A confirmation
                has been emailed to you and your driver will be in touch on the day of travel.
              </p>
              {state.amount > 0 && (
                <p className="mt-6 inline-block px-4 py-2 rounded-full border border-[#C5A059]/30 bg-[#C5A059]/5 text-[#C5A059] text-sm">
                  {eur(state.amount)} paid
                </p>
              )}
              <div className="mt-10">
                <Link
                  to="/"
                  data-testid="deposit-home-link"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black text-sm font-semibold uppercase tracking-[0.15em] transition-colors"
                >
                  <Home className="w-4 h-4" /> Back to home
                </Link>
              </div>
            </div>
          )}

          {state.phase === "pending" && (
            <div className="py-16">
              <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin mx-auto" />
              <h1 className="mt-6 font-display text-3xl text-white font-light">Payment is processing</h1>
              <p className="mt-3 text-sm text-zinc-400">
                Your bank is confirming the transaction. Once processed, we&rsquo;ll email you a receipt.
                You can safely close this page.
              </p>
            </div>
          )}

          {state.phase === "expired" && (
            <div className="py-16">
              <XCircle className="w-10 h-10 text-red-400 mx-auto" />
              <h1 className="mt-6 font-display text-3xl text-white font-light">Session expired</h1>
              <p className="mt-3 text-sm text-zinc-400">
                Your Stripe checkout session expired before completing. Please try again from your booking link.
              </p>
            </div>
          )}

          {state.phase === "error" && (
            <div className="py-16">
              <XCircle className="w-10 h-10 text-red-400 mx-auto" />
              <h1 className="mt-6 font-display text-3xl text-white font-light">Something went wrong</h1>
              <p className="mt-3 text-sm text-zinc-400">{state.message}</p>
            </div>
          )}

          {state.phase === "missing" && (
            <div className="py-16 text-sm text-zinc-500">
              No payment session found in the URL.
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
